import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutWrapper from '../components/LayoutWrapper';
import ResetSignupModal from '../components/ResetSignupModal';
import { StalledSignUpsView, InvitationResultsView } from '../components/SignUpReportViews';
import Helper from '../helpers';
import config from '../config';
import { getSubscriptions } from '../Services/VenueService.ts';

const baseUrl = config.apiBaseUrl;

// Two reports for venue admins in one place:
//   Stalled sign ups: members who began a Direct Debit and never finished
//   Invitation results: how each invitation turned out
const SignUpHealthReport = () => {
    const navigate = useNavigate();
    const [tab, setTab] = useState('stalled');
    const [venueKey, setVenueKey] = useState('');
    const [stalled, setStalled] = useState(null);
    const [invitations, setInvitations] = useState(null);
    const [includeNeverStarted, setIncludeNeverStarted] = useState(false);
    const [days, setDays] = useState(90);
    const [plans, setPlans] = useState([]);
    const [resetMember, setResetMember] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const get = useCallback(async (path) => {
        const token = await Helper.getCookie('wh-jwt');
        const response = await fetch(`${baseUrl}${path}`, {
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            credentials: 'include'
        });
        if (!response.ok) throw new Error('Could not load the report.');
        return response.json();
    }, []);

    useEffect(() => {
        const init = async () => {
            const vk = await Helper.getUserVenueId();
            setVenueKey(vk);
            if (vk) {
                try {
                    const subs = await getSubscriptions(vk);
                    setPlans((subs?.items || []).filter(s => !s.isImported && !s.isCustomerSubscription));
                } catch (e) {
                    console.error('Could not load plans for the sign up link', e);
                }
            }
        };
        init();
    }, []);

    useEffect(() => {
        if (!venueKey) return;
        const load = async () => {
            setLoading(true);
            setError('');
            try {
                if (tab === 'stalled') {
                    setStalled(await get(`/api/venues/${venueKey}/reports/stalled-signups?includeNeverStarted=${includeNeverStarted}`));
                } else {
                    setInvitations(await get(`/api/venues/${venueKey}/reports/invitation-results?days=${days}`));
                }
            } catch (e) {
                setError(e.message || 'Could not load the report.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [venueKey, tab, includeNeverStarted, days, get]);

    const tabClass = (name) => `px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
        tab === name ? 'bg-emerald-600 text-white' : 'bg-luxury-obsidian text-emerald-400 hover:bg-emerald-900/30'}`;

    return (
        <LayoutWrapper title="Sign up health" subtitle="Members who did not finish signing up, and how your invitations turned out">
            <div className="flex gap-2 mb-6">
                <button className={tabClass('stalled')} onClick={() => setTab('stalled')}>Stalled sign ups</button>
                <button className={tabClass('invitations')} onClick={() => setTab('invitations')}>Invitation results</button>
            </div>

            {error && (
                <div className="mb-4 p-4 rounded-xl border border-performance-pulse/40 bg-performance-pulse/10 text-sm text-performance-pulse">{error}</div>
            )}

            {loading ? (
                <div className="flex justify-center items-center h-40">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-500" />
                </div>
            ) : tab === 'stalled' ? (
                stalled && (
                    <StalledSignUpsView
                        report={stalled}
                        includeNeverStarted={includeNeverStarted}
                        onToggleNeverStarted={setIncludeNeverStarted}
                        onSendLink={setResetMember}
                        onLinkGoCardless={() => navigate('/dashboard/import-users/gocardless')}
                    />
                )
            ) : (
                invitations && <InvitationResultsView report={invitations} days={days} onDaysChange={setDays} />
            )}

            {resetMember && (
                <ResetSignupModal member={resetMember} venueKey={venueKey} plans={plans} onClose={() => setResetMember(null)} />
            )}
        </LayoutWrapper>
    );
};

export default SignUpHealthReport;
